/**
 * Shapes shared by the generator, the solver, and the UI.
 *
 * A Light and Shadow board is a grid split into light and shadow cells. Every
 * connected block of one shade is a *region*, every region holds exactly one
 * number, and that number is the region's area. Two regions of the same shade
 * therefore never share an edge — if they did they would be one region with two
 * numbers in it.
 *
 * That last rule is why the solution is only a shading: colour the cells and the
 * regions are whatever the shading makes them. Nothing else has to be stored,
 * and nothing can disagree with anything.
 */

/** A cell the player has not decided yet. Never appears in a solution. */
export const UNKNOWN = 0;
export const LIGHT = 1;
export const SHADOW = 2;

export type Shade = typeof LIGHT | typeof SHADOW;
export type Mark = typeof UNKNOWN | Shade;

export function other(shade: Shade): Shade {
  return shade === LIGHT ? SHADOW : LIGHT;
}

export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';

export const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  expert: 'Expert',
};

/**
 * The board each tier is played on.
 *
 * Size is part of the difficulty here rather than a setting of its own: a bigger
 * grid holds more regions, and a region can only be pinned down by the ones
 * around it, so the reasoning gets longer as much as it gets harder. The tiers
 * also have to differ in kind — see `grade` in `solver.ts` — and a five-wide
 * board rarely has room for the reasoning the top tiers are graded on.
 *
 * No board goes over 64 cells, and that is a hard ceiling rather than a taste:
 * the solver works in bitmasks of two 32-bit words, which is what makes trying
 * thousands of candidate regions cheap enough to do inside a generation loop.
 */
export const BOARDS: Record<Difficulty, { width: number; height: number }> = {
  easy: { width: 5, height: 6 },
  medium: { width: 6, height: 7 },
  hard: { width: 7, height: 8 },
  expert: { width: 8, height: 8 },
};

/** The ceiling the masks impose, asserted where boards are built. */
export const MAX_CELLS = 64;

/**
 * The largest number that can appear on a board.
 *
 * Every region of size n has to be enumerated as every n-cell polyomino that
 * could hold its number, and that count grows about fourfold per cell: 760 fixed
 * heptominoes against 2725 octominoes. Seven is where enumeration is still
 * instant, and a seven-cell region is already a shape a player has to think
 * about.
 */
export const MAX_REGION = 7;

/**
 * A number printed on the board, and the shade its region is drawn in.
 *
 * `value` is null for a `?`: a region whose size the player is not told. It is
 * null rather than a flag beside the real size so that nothing can read the
 * answer by accident — every place that wants the size has to say what it does
 * without one, and the compiler asks. The size a `?` hides is still in
 * `solution` like every other, which is the one copy anything is allowed to
 * reveal from.
 *
 * A `?` is not unbounded: it is a number from 1 to `MAX_REGION`, the same range
 * every printed number comes from. That range is what the solver enumerates and
 * therefore what uniqueness is proved against, so it is part of the rules the
 * player is told rather than an implementation detail.
 */
export interface Clue {
  cell: number;
  value: number | null;
  shade: Shade;
}

/** A generated board. `solution` is kept for the reveal, never for checking. */
export interface Puzzle {
  width: number;
  height: number;
  clues: Clue[];
  /** LIGHT or SHADOW for every cell, row-major: the one solution. */
  solution: Shade[];
  /** The difficulty the generator actually achieved, which can miss the ask. */
  difficulty: Difficulty;
  seed: number;
}

export function cellCount(puzzle: { width: number; height: number }): number {
  return puzzle.width * puzzle.height;
}
